import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { getSeverityClasses } from './severityStyles'
import { canonicalBrandName } from '../../lib/utils'

export interface ClusterSignalDetail {
  id?: number
  description?: string
  severity?: string
  channel?: string
}

export interface SignalCluster {
  id: number | string
  cluster_date?: string
  competitor_id: number
  competitor_name: string
  cluster_type: string
  primary_channel: string
  signal_count: number
  max_severity: string
  narrative: string
  impact_score?: number
  signal_ids: number[]
  metadata?: {
    signals?: ClusterSignalDetail[]
    channels?: string[]
    families?: Array<{ keyword_family: string; count: number }>
    [key: string]: unknown
  }
}

interface ClusterCardProps {
  cluster: SignalCluster
  onExpand?: (clusterId: number | string, expanded: boolean) => void
  staleLabel?: string | null
  actionTaken?: boolean
  actionFinding?: string | null
}

function channelLabel(channel: string): string {
  const normalized = channel.toLowerCase()
  if (normalized === 'google_shopping') return 'Google Shopping'
  if (normalized === 'cross_platform') return 'Cross-Platform'
  if (normalized === 'amazon') return 'Amazon'
  if (normalized === 'tiktok') return 'TikTok'
  return channel
}

function clusterTypeMeta(clusterType: string): { icon: string; label: string } | null {
  const normalized = clusterType.toLowerCase()
  if (normalized === 'creator_convergence') {
    return { icon: '🧑‍🤝‍🧑', label: 'Creator Convergence' }
  }
  if (normalized === 'paid_creator_campaign') {
    return { icon: '💸', label: 'Paid Creator Campaign' }
  }
  if (normalized === 'tiktok_ad_escalation') {
    return { icon: '📈', label: 'Ad Escalation' }
  }
  return null
}

function formatThreatScore(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—'
  }
  return Math.round(Number(value)).toLocaleString('en-US')
}

export default function ClusterCard({ cluster, onExpand, staleLabel, actionTaken = false, actionFinding }: ClusterCardProps) {
  const [expanded, setExpanded] = useState(false)
  const styles = getSeverityClasses(cluster.max_severity)
  const typeMeta = clusterTypeMeta(cluster.cluster_type)
  const competitorDisplayName = canonicalBrandName(cluster.competitor_name) || 'Global Signal'

  const details = useMemo<ClusterSignalDetail[]>(() => {
    if (Array.isArray(cluster.metadata?.signals) && cluster.metadata?.signals.length > 0) {
      return cluster.metadata.signals
    }

    return (cluster.signal_ids ?? []).map((signalId) => ({ id: signalId } as ClusterSignalDetail))
  }, [cluster.metadata?.signals, cluster.signal_ids])

  const toggleExpanded = () => {
    const next = !expanded
    setExpanded(next)
    onExpand?.(cluster.id, next)
  }

  return (
    <article className={`group rounded-xl border p-4 shadow-sm ${styles.border} ${styles.bg}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <p className="truncate text-lg font-bold text-slate-900">{competitorDisplayName}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold uppercase ${styles.badge} ${styles.border}`}>
              {cluster.max_severity}
            </span>
            <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-600">
              {channelLabel(cluster.primary_channel)}
            </span>
            {typeMeta && (
              <span className="rounded-full border border-[var(--brand-200)] bg-[var(--brand-50)] px-2 py-0.5 text-xs font-semibold text-[var(--brand-700)]">
                {typeMeta.icon} {typeMeta.label}
              </span>
            )}
            {actionTaken ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Action taken
              </span>
            ) : null}
            {staleLabel ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" />
                {staleLabel}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-slate-700">{cluster.narrative}</p>
          {actionTaken && actionFinding ? (
            <p className={`text-xs text-emerald-700 ${expanded ? '' : 'hidden group-hover:block'}`}>
              Action note: {actionFinding}
            </p>
          ) : null}
        </div>

        <div className="shrink-0 text-right">
          <p className="text-3xl font-bold text-slate-900">{formatThreatScore(cluster.impact_score)}</p>
          <p className="text-xs text-slate-500">threat score</p>
          <p className="mt-1 text-xs font-semibold text-slate-700">
            {cluster.signal_count} signal{cluster.signal_count === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-200 pt-3">
        <button
          type="button"
          onClick={toggleExpanded}
          className="text-xs font-semibold text-[var(--patterniq-navy-900)] hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          {expanded ? 'Hide underlying signals' : 'Show underlying signals'}
        </button>

        {Array.isArray(cluster.metadata?.families) && cluster.metadata?.families.length > 0 && (
          <span className="text-xs text-slate-600">{cluster.metadata.families.length} keyword families</span>
        )}
      </div>

      {expanded && (
        <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
          {details.length === 0 ? (
            <p className="text-xs text-slate-500">No underlying signals available.</p>
          ) : (
            details.map((detail, index) => (
              <div key={`${cluster.id}-${detail.id ?? 'signal'}-${index}`} className="rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2">
                <div className="text-xs font-medium text-slate-700">Signal #{detail.id ?? index + 1}</div>
                {detail.description && <div className="mt-1 text-xs text-slate-600">{detail.description}</div>}
              </div>
            ))
          )}
        </div>
      )}
    </article>
  )
}
